import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  HardDrive, FileText, FileCode2, Loader2, RefreshCw, Trash2, AlertTriangle,
  Search, ChevronLeft, ChevronRight, Package, Receipt, ExternalLink,
  Cloud, Server, KeyRound, ArrowRight, CheckCircle2, XCircle, Info, Save, X,
} from "lucide-react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import { supportCode } from "@/lib/supportCode";
import ConfirmDialog from "@/components/ConfirmDialog";

const PAGE_SIZE = 10;

const DIR_META = {
  uploads:               { icon: FileText,  tone: "text-[#1D4ED8] bg-blue-50" },
  xml_output:            { icon: FileCode2, tone: "text-emerald-700 bg-emerald-50" },
  platform_invoices_xml: { icon: Receipt,   tone: "text-[#B8985C] bg-[#B8985C]/10" },
  proforma_pdfs:         { icon: Package,   tone: "text-[#F97316] bg-[#F97316]/10" },
};

function DiskGauge({ pct }) {
  const p = Math.min(100, Math.max(0, Number(pct) || 0));
  const tone =
    p >= 90 ? "bg-red-500" : p >= 75 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="w-full h-2 rounded-full bg-[#E8E4D9] overflow-hidden" data-testid="disk-gauge">
      <div className={`h-full ${tone}`} style={{ width: `${p}%` }} />
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminStorage() {
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [activeDir, setActiveDir] = useState("uploads");
  const [only, setOnly] = useState("all"); // all | orphans | linked
  const [location, setLocation] = useState("all"); // all | local | s3
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [query, setQuery] = useState("");
  const [files, setFiles] = useState({ items: [], total: 0, orphan_count: 0 });
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [page, setPage] = useState(0);
  const [confirmDel, setConfirmDel] = useState(null); // { filename, is_linked }
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showMigrate, setShowMigrate] = useState(false);
  const [showRepair, setShowRepair] = useState(false);
  const [showKeyRepair, setShowKeyRepair] = useState(false);
  const [debugResult, setDebugResult] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const runDebug = async () => {
    setShowDebug(true);
    setDebugResult(null);
    try {
      const { data } = await api.get("/admin/storage/debug");
      setDebugResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore diagnostica");
      setDebugResult({ error: e?.response?.data?.detail || "Errore diagnostica" });
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const { data } = await api.get("/admin/storage/stats");
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile caricare stats storage");
    } finally {
      setLoadingStats(false);
    }
  };

  const loadFiles = async () => {
    setLoadingFiles(true);
    try {
      const params = { directory: activeDir, only, location, limit: 500, offset: 0 };
      if (query.trim()) params.q = query.trim();
      const { data } = await api.get("/admin/storage/files", { params });
      setFiles(data);
      setPage(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento file");
      setFiles({ items: [], total: 0, orphan_count: 0 });
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadFiles(); }, [activeDir, only, location]);
  // Auto-refresh ogni 20s se abilitato — utile per vedere upload real-time
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { loadStats(); loadFiles(); }, 20000);
    return () => clearInterval(t);
  }, [autoRefresh, activeDir, only, location, query]);
  // Debounce leggero su cambi di query
  useEffect(() => {
    const t = setTimeout(() => loadFiles(), 250);
    return () => clearTimeout(t);
  }, [query]);

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      const params = { force: !!confirmDel.is_linked };
      if (confirmDel.marker) params.marker = confirmDel.marker;
      else { params.directory = activeDir; params.filename = confirmDel.filename; }
      const { data } = await api.delete("/admin/storage/files", { params });
      toast.success(`File eliminato · ${data.size_bytes} B liberati`);
      setConfirmDel(null);
      await Promise.all([loadStats(), loadFiles()]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Errore delete");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const doCleanup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/storage/cleanup-orphans", null, {
        params: { directory: activeDir },
      });
      if (data.deleted > 0) {
        toast.success(`${data.deleted} file orfani eliminati · ${data.bytes_human} liberati`);
      } else {
        toast.info("Nessun file orfano da eliminare");
      }
      setConfirmCleanup(false);
      await Promise.all([loadStats(), loadFiles()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cleanup");
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((files.items || []).length / PAGE_SIZE));
  const pageStart = page * PAGE_SIZE;
  const paged = useMemo(
    () => (files.items || []).slice(pageStart, pageStart + PAGE_SIZE),
    [files.items, pageStart]
  );

  const activeMeta = stats?.dirs?.find((d) => d.key === activeDir);

  return (
    <div data-testid="admin-storage">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-2">
            <HardDrive className="w-3.5 h-3.5" /> Storage & file
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Storage</h1>
          <p className="text-sm text-[#737373] mt-1 max-w-2xl">
            Spazio occupato per directory + file browser. I file <strong>orfani</strong> non hanno un documento DB collegato — possono essere rimossi senza rischi.
          </p>
          {/* Backend attivo badge */}
          {stats?.backend && (
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="storage-backend-badge">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                stats.backend.provider === "s3" ? "border-orange-300 bg-orange-100 text-orange-800" : "border-zinc-300 bg-zinc-100 text-zinc-700"
              }`}>
                {stats.backend.provider === "s3" ? <Cloud className="w-3 h-3" /> : <Server className="w-3 h-3" />}
                Backend attivo: {stats.backend.provider === "s3" ? (stats.backend.s3?.provider_kind === "cloudflare_r2" ? "Cloudflare R2" : "S3") : "Locale"}
              </span>
              {stats.backend.provider === "s3" && stats.backend.s3?.bucket_name && (
                <span className="text-[11px] text-[#737373] font-mono">
                  bucket · <strong className="text-[#0A0A0A]">{stats.backend.s3.bucket_name}</strong>
                </span>
              )}
              {stats.backend.provider === "s3" && !stats.backend.ready && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded uppercase">
                  <AlertTriangle className="w-3 h-3" /> config incompleta
                </span>
              )}
            </div>
          )}
          {/* S3 errors banner — surfacing di errori list_dir per aiutare l'admin */}
          {stats?.errors && Object.keys(stats.errors).length > 0 && (
            <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-900" data-testid="storage-errors-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-bold text-xs uppercase tracking-wider">Errori nel listing S3</div>
                <ul className="text-xs mt-1 space-y-0.5">
                  {Object.entries(stats.errors).map(([dir, msg]) => (
                    <li key={dir}><code className="font-mono">{dir}</code>: {msg}</li>
                  ))}
                </ul>
                <button
                  onClick={runDebug}
                  data-testid="storage-diagnose-inline"
                  className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-red-800 hover:underline uppercase tracking-wider"
                >
                  <Info className="w-3 h-3" /> Esegui diagnostica completa →
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowConfig(true)}
            data-testid="storage-config-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#0A0A0A] bg-[#0A0A0A] text-white text-sm hover:bg-[#1D4ED8] transition-colors"
          >
            <KeyRound className="w-3.5 h-3.5" /> Configura backend
          </button>
          {stats?.backend?.provider === "s3" && (stats?.total_local_files || 0) > 0 && (
            <button
              onClick={() => setShowMigrate(true)}
              data-testid="storage-migrate-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-orange-400 bg-orange-50 text-orange-800 text-sm hover:bg-orange-100 transition-colors font-semibold"
              title={`${stats.total_local_files} file locali · ${stats.total_local_human}`}
            >
              <ArrowRight className="w-3.5 h-3.5" /> Migra {stats.total_local_files} file locali → S3
            </button>
          )}
          <button
            onClick={() => setShowRepair(true)}
            data-testid="storage-repair-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-emerald-400 bg-emerald-50 text-emerald-800 text-sm hover:bg-emerald-100 transition-colors font-semibold"
            title="Bulk-rigenera XML mancanti (post-migrazione)"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Ripara XML
          </button>
          {stats?.backend?.provider === "s3" && (
            <button
              onClick={() => setShowKeyRepair(true)}
              data-testid="storage-repair-keys-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-purple-400 bg-purple-50 text-purple-800 text-sm hover:bg-purple-100 transition-colors font-semibold"
              title="Rinomina key S3 mal-prefissate (post-migration bug fix)"
            >
              <KeyRound className="w-3.5 h-3.5" /> Ripara key S3
            </button>
          )}
          {stats?.backend?.provider === "s3" && (
            <button
              onClick={runDebug}
              data-testid="storage-diagnose-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]"
            >
              <Info className="w-3.5 h-3.5" /> Diagnostica S3
            </button>
          )}
          <button
            onClick={() => { loadStats(); loadFiles(); }}
            data-testid="storage-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Ricarica
          </button>
        </div>
      </div>

      {/* Global stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="storage-stat-total">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Occupato dall&apos;app</div>
          <div className="font-display font-extrabold text-3xl tracking-tight text-[#0A0A0A] mt-1 tabular-nums">
            {loadingStats ? "—" : (stats?.total_human || "0 B")}
          </div>
          <div className="text-xs text-[#737373] mt-1">
            {stats?.total_files ?? 0} file totali · <span className="text-amber-700 font-semibold">{stats?.total_orphans ?? 0} orfani</span>
          </div>
        </div>
        <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="storage-stat-orphans">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Spazio recuperabile</div>
          <div className="font-display font-extrabold text-3xl tracking-tight text-[#F97316] mt-1 tabular-nums">
            {loadingStats ? "—" : (stats?.total_orphan_human || "0 B")}
          </div>
          <div className="text-xs text-[#737373] mt-1">
            File orfani su tutte le directory
          </div>
        </div>
        <div className="rounded-2xl border border-[#E8E4D9] bg-white p-5" data-testid="storage-stat-disk">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">Disco container</div>
          {stats?.disk ? (
            <>
              <div className="font-display font-extrabold text-3xl tracking-tight text-[#0A0A0A] mt-1 tabular-nums">
                {stats.disk.used_pct}%
              </div>
              <div className="text-xs text-[#737373] mt-1">
                {stats.disk.used_human} / {stats.disk.total_human} · {stats.disk.free_human} liberi
              </div>
              <div className="mt-2"><DiskGauge pct={stats.disk.used_pct} /></div>
            </>
          ) : (
            <div className="text-sm text-[#737373] italic mt-2">Info disco non disponibile</div>
          )}
        </div>
      </div>

      {/* Directory tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-4" data-testid="storage-dir-tabs">
        {(stats?.dirs || []).map((d) => {
          const meta = DIR_META[d.key] || { icon: FileText, tone: "text-zinc-700 bg-zinc-100" };
          const Icon = meta.icon;
          const active = d.key === activeDir;
          return (
            <button
              key={d.key}
              onClick={() => setActiveDir(d.key)}
              data-testid={`storage-tab-${d.key}`}
              className={`text-left rounded-xl border p-4 transition-all ${
                active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white shadow-md" : "border-[#E8E4D9] bg-white hover:border-[#0A0A0A]/40"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${active ? "bg-white/10" : meta.tone}`}>
                  <Icon className="w-4 h-4" />
                </span>
                {d.orphan_count > 0 && (
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    active ? "bg-amber-500/30 text-amber-100" : "bg-amber-100 text-amber-800"
                  }`}>
                    {d.orphan_count} orfani
                  </span>
                )}
              </div>
              <div className={`text-[10px] uppercase tracking-widest font-bold ${active ? "text-white/60" : "text-[#737373]"}`}>
                {d.key}
              </div>
              <div className={`font-display font-bold text-sm mt-0.5 truncate ${active ? "text-white" : "text-[#0A0A0A]"}`}>
                {d.label}
              </div>
              <div className={`text-xs mt-1.5 font-mono tabular-nums ${active ? "text-white/70" : "text-[#4A4A4A]"}`}>
                {d.count} file · <span className="font-semibold">{d.human}</span>
              </div>
              {(d.local_count > 0 || d.s3_count > 0) && (
                <div className={`text-[10px] mt-1 flex items-center gap-1.5 flex-wrap ${active ? "text-white/60" : "text-[#737373]"}`}>
                  {d.local_count > 0 && (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-bold uppercase ${
                      active ? "bg-white/10 text-white/80" : "bg-zinc-100 text-zinc-700"
                    }`}>
                      <Server className="w-2.5 h-2.5" /> {d.local_count} local
                    </span>
                  )}
                  {d.s3_count > 0 && (
                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-bold uppercase ${
                      active ? "bg-orange-500/30 text-orange-100" : "bg-orange-100 text-orange-800"
                    }`}>
                      <Cloud className="w-2.5 h-2.5" /> {d.s3_count} S3
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white p-3 mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="storage-search"
            placeholder="Cerca filename…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono focus:border-[#0A0A0A] outline-none"
          />
        </div>
        <select
          value={only}
          onChange={(e) => setOnly(e.target.value)}
          data-testid="storage-filter"
          className="h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-medium"
          title="Filtro per stato DB (orfano = nessun documento lo referenzia)"
        >
          <option value="all">Stato: Tutti</option>
          <option value="orphans">Solo orfani</option>
          <option value="linked">Solo collegati</option>
        </select>
        <select
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          data-testid="storage-location-filter"
          className="h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-medium"
          title="Filtro per posizione fisica del file"
        >
          <option value="all">Location: Tutti</option>
          <option value="local">Solo Local</option>
          <option value="s3">Solo S3</option>
        </select>
        <label
          className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm cursor-pointer hover:bg-[#F5F2EA]"
          data-testid="storage-autorefresh-toggle"
          title="Ricarica automatica ogni 20 secondi"
        >
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="w-3.5 h-3.5 accent-emerald-600"
          />
          <span className="text-xs font-semibold">
            {autoRefresh ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                LIVE 20s
              </span>
            ) : "Auto-refresh"}
          </span>
        </label>
        {activeMeta?.orphan_count > 0 && (
          <button
            onClick={() => setConfirmCleanup(true)}
            data-testid="storage-cleanup-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F97316] text-white text-sm font-semibold hover:bg-[#EA580C] transition-colors ml-auto"
          >
            <AlertTriangle className="w-4 h-4" />
            Elimina {activeMeta.orphan_count} orfani ({activeMeta.orphan_human})
          </button>
        )}
      </div>

      {/* File table */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden">
        {loadingFiles ? (
          <div className="p-16 grid place-items-center">
            <Loader2 className="w-6 h-6 text-[#737373] animate-spin" />
          </div>
        ) : (files.items || []).length === 0 ? (
          <div className="p-10 text-center text-sm text-[#737373]">
            {query ? "Nessun file corrisponde alla ricerca." : "Nessun file in questa directory."}
          </div>
        ) : (
          <>
            <table className="w-full text-sm" data-testid="storage-table">
              <thead>
                <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
                  <th className="text-left px-4 py-3">Filename</th>
                  <th className="text-left px-4 py-3 w-32">Dimensione</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell w-44">Modificato</th>
                  <th className="text-left px-4 py-3">Documento collegato</th>
                  <th className="text-right px-4 py-3 w-20">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((row, i) => (
                  <tr
                    key={row.filename}
                    data-testid={`storage-row-${pageStart + i}`}
                    className={`${i > 0 ? "border-t border-[#E8E4D9]/70" : ""} ${row.is_orphan ? "bg-amber-50/40" : ""}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <div className="flex items-center gap-2">
                        {row.is_orphan && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
                            title="Nessun documento DB referenzia questo file"
                          >
                            Orfano
                          </span>
                        )}
                        {row.location === "s3" ? (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[9px] font-bold uppercase tracking-wider"
                            title={row.marker}
                          >
                            <Cloud className="w-2.5 h-2.5" /> S3
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[9px] font-bold uppercase tracking-wider"
                            title={row.marker}
                          >
                            <Server className="w-2.5 h-2.5" /> local
                          </span>
                        )}
                        <span className="truncate max-w-[240px]" title={row.filename}>{row.filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums font-mono text-xs">{row.size_human}</td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-xs text-[#737373]">{fmtDate(row.mtime)}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {row.linked_doc ? (
                        <LinkedDocInfo dir={activeDir} doc={row.linked_doc} />
                      ) : (
                        <span className="italic text-[#A3A3A3]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setConfirmDel({ filename: row.filename, marker: row.marker, is_linked: !row.is_orphan })}
                        data-testid={`storage-delete-${pageStart + i}`}
                        title={row.is_orphan ? "Elimina file orfano" : "Elimina file (forza)"}
                        className={`inline-flex items-center px-2 py-1 rounded text-[10px] font-semibold ${
                          row.is_orphan ? "text-red-700 hover:bg-red-50" : "text-[#4A4A4A] hover:bg-red-50 hover:text-red-700"
                        }`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div
                className="border-t border-[#E8E4D9] px-4 py-3 flex items-center justify-between text-sm bg-[#FAF9F6]"
                data-testid="storage-pagination"
              >
                <div className="text-[#737373]">
                  Pagina <span className="font-semibold text-[#0A0A0A]">{page + 1}</span> di{" "}
                  <span className="font-semibold text-[#0A0A0A]">{totalPages}</span>
                  <span className="hidden sm:inline text-[#A3A3A3] ml-2">
                    ({pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, files.items.length)} su {files.items.length})
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="storage-prev-page"
                    className="inline-flex items-center gap-1 h-8 pl-2 pr-3 rounded-full border border-[#E8E4D9] bg-white hover:bg-[#F5F2EA] disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Precedente
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    data-testid="storage-next-page"
                    className="inline-flex items-center gap-1 h-8 pl-3 pr-2 rounded-full border border-[#E8E4D9] bg-white hover:bg-[#F5F2EA] disabled:opacity-40 transition-colors"
                  >
                    Successiva <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doDelete}
        loading={busy}
        icon={Trash2}
        tone="danger"
        title={confirmDel?.is_linked ? "Eliminare file COLLEGATO?" : "Eliminare file orfano?"}
        description={
          confirmDel?.is_linked ? (
            <>
              <strong>Attenzione:</strong> questo file è ancora referenziato da un documento nel DB.
              Se lo elimini, il download utente/admin fallirà (500/410) finché il file non viene rigenerato.
              Procedi solo se sai cosa stai facendo.
            </>
          ) : (
            <>
              File orfano — nessun documento DB lo referenzia. Eliminazione sicura. Operazione irreversibile.
            </>
          )
        }
        confirmLabel="Sì, elimina"
      />
      <ConfirmDialog
        open={confirmCleanup}
        onOpenChange={setConfirmCleanup}
        onConfirm={doCleanup}
        loading={busy}
        icon={AlertTriangle}
        tone="danger"
        title={`Eliminare ${activeMeta?.orphan_count || 0} file orfani?`}
        description={
          <>
            Sto per eliminare <strong>{activeMeta?.orphan_count || 0}</strong> file orfani in{" "}
            <code className="font-mono text-xs bg-[#F5F2EA] px-1 rounded">{activeDir}</code>,
            per un totale di <strong>{activeMeta?.orphan_human || "0 B"}</strong>.<br />
            Nessun documento DB verrà modificato. Operazione irreversibile.
          </>
        }
        confirmLabel={`Sì, libera ${activeMeta?.orphan_human || "0 B"}`}
      />

      {showConfig && (
        <StorageConfigModal
          onClose={() => setShowConfig(false)}
          onSaved={() => { setShowConfig(false); loadStats(); loadFiles(); }}
        />
      )}
      {showMigrate && (
        <StorageMigrateModal
          onClose={() => setShowMigrate(false)}
          onDone={() => { setShowMigrate(false); loadStats(); loadFiles(); }}
        />
      )}
      {showDebug && (
        <StorageDebugModal
          result={debugResult}
          onClose={() => setShowDebug(false)}
          onRerun={runDebug}
        />
      )}
      {showRepair && (
        <StorageRepairModal
          onClose={() => setShowRepair(false)}
          onDone={() => { setShowRepair(false); loadStats(); loadFiles(); }}
        />
      )}
      {showKeyRepair && (
        <StorageKeyRepairModal
          onClose={() => setShowKeyRepair(false)}
          onDone={() => { setShowKeyRepair(false); loadStats(); loadFiles(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────  StorageConfigModal  ──────────────────────────

function StorageConfigModal({ onClose, onSaved }) {
  const [cfg, setCfg] = useState({
    provider: "local",
    s3: {
      provider_kind: "cloudflare_r2",
      endpoint_url: "",
      region: "auto",
      access_key_id: "",
      secret_access_key: "",
      bucket_name: "",
      public_url_base: "",
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [lastMeta, setLastMeta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/storage/config");
        setLastMeta({
          updated_at: data.updated_at,
          last_tested_at: data.last_tested_at,
          last_test_ok: data.last_test_ok,
          last_test_msg: data.last_test_msg,
        });
        if (data.provider === "s3" && data.s3) {
          setCfg({
            provider: "s3",
            s3: {
              provider_kind: data.s3.provider_kind || "cloudflare_r2",
              endpoint_url: data.s3.endpoint_url || "",
              region: data.s3.region || "auto",
              // Le chiavi tornano MASCHERATE dal backend — le lasciamo vuote
              // così l'admin le reinserisce solo se vuole cambiarle.
              access_key_id: "",
              secret_access_key: "",
              bucket_name: data.s3.bucket_name || "",
              public_url_base: data.s3.public_url_base || "",
            },
          });
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento config");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updS3 = (k) => (e) => {
    const v = e.target?.value ?? e;
    setCfg((p) => ({ ...p, s3: { ...p.s3, [k]: v } }));
  };

  const applyPreset = (kind) => {
    if (kind === "cloudflare_r2") {
      setCfg((p) => ({ ...p, s3: { ...p.s3, provider_kind: "cloudflare_r2", region: "auto" } }));
    } else if (kind === "aws_s3") {
      setCfg((p) => ({ ...p, s3: { ...p.s3, provider_kind: "aws_s3", region: p.s3.region || "eu-central-1", endpoint_url: p.s3.endpoint_url || "https://s3.amazonaws.com" } }));
    } else {
      setCfg((p) => ({ ...p, s3: { ...p.s3, provider_kind: "generic" } }));
    }
  };

  const testCurrent = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/storage/config/test", cfg);
      setTestResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore test";
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/storage/config", cfg);
      toast.success("Configurazione salvata");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const s3Ready = cfg.provider === "s3" && cfg.s3.endpoint_url && cfg.s3.access_key_id && cfg.s3.secret_access_key && cfg.s3.bucket_name;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="storage-config-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold">
              Storage · Configurazione backend
            </div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight mt-0.5">Backend di storage</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md" data-testid="storage-config-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#737373]" /></div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Provider selector */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCfg((p) => ({ ...p, provider: "local" }))}
                data-testid="storage-provider-local"
                className={`text-left p-4 rounded-xl border transition-all ${
                  cfg.provider === "local" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E8E4D9] bg-white hover:border-[#0A0A0A]/40"
                }`}
              >
                <Server className={`w-5 h-5 mb-2 ${cfg.provider === "local" ? "text-white" : "text-[#0A0A0A]"}`} />
                <div className="font-display font-bold text-sm">Filesystem locale</div>
                <div className={`text-xs mt-1 ${cfg.provider === "local" ? "text-white/70" : "text-[#737373]"}`}>
                  I file vivono su `/app/backend/*`. Semplice ma legato al container.
                </div>
              </button>
              <button
                type="button"
                onClick={() => setCfg((p) => ({ ...p, provider: "s3" }))}
                data-testid="storage-provider-s3"
                className={`text-left p-4 rounded-xl border transition-all ${
                  cfg.provider === "s3" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E8E4D9] bg-white hover:border-[#0A0A0A]/40"
                }`}
              >
                <Cloud className={`w-5 h-5 mb-2 ${cfg.provider === "s3" ? "text-white" : "text-orange-600"}`} />
                <div className="font-display font-bold text-sm">S3-compatible</div>
                <div className={`text-xs mt-1 ${cfg.provider === "s3" ? "text-white/70" : "text-[#737373]"}`}>
                  Cloudflare R2 / AWS S3 / MinIO. Durevole, scalabile, indipendente dal container.
                </div>
              </button>
            </div>

            {cfg.provider === "s3" && (
              <>
                {/* Presets */}
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#737373] font-bold mb-2">Provider S3</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[
                      { k: "cloudflare_r2", label: "Cloudflare R2" },
                      { k: "aws_s3",        label: "AWS S3" },
                      { k: "generic",       label: "Generico / MinIO" },
                    ].map((p) => (
                      <button
                        key={p.k}
                        type="button"
                        onClick={() => applyPreset(p.k)}
                        data-testid={`storage-preset-${p.k}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                          cfg.s3.provider_kind === p.k ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E8E4D9] bg-white hover:bg-[#F5F2EA]"
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {cfg.s3.provider_kind === "cloudflare_r2" && (
                    <p className="text-[11px] text-[#737373] mt-2 leading-snug flex gap-1.5">
                      <Info className="w-3 h-3 mt-0.5 shrink-0" />
                      Endpoint R2: <code className="font-mono text-[#0A0A0A]">https://&lt;account_id&gt;.r2.cloudflarestorage.com</code>.
                      Trovi tutto in <em>Cloudflare dashboard → R2 → Manage R2 API Tokens</em>.
                    </p>
                  )}
                </div>

                {/* S3 fields */}
                <div className="space-y-3">
                  <Field label="Endpoint URL" testid="s3-endpoint">
                    <input
                      value={cfg.s3.endpoint_url}
                      onChange={updS3("endpoint_url")}
                      placeholder="https://<account_id>.r2.cloudflarestorage.com"
                      className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Region" testid="s3-region">
                      <input
                        value={cfg.s3.region}
                        onChange={updS3("region")}
                        placeholder="auto"
                        className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                      />
                    </Field>
                    <Field label="Bucket" testid="s3-bucket">
                      <input
                        value={cfg.s3.bucket_name}
                        onChange={updS3("bucket_name")}
                        placeholder="autofattura"
                        className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                      />
                    </Field>
                  </div>
                  <Field label="Access Key ID" testid="s3-akid">
                    <input
                      value={cfg.s3.access_key_id}
                      onChange={updS3("access_key_id")}
                      placeholder="Nuovo · lascia vuoto per non cambiare"
                      className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                    />
                  </Field>
                  <Field label="Secret Access Key" testid="s3-sak">
                    <div className="relative">
                      <input
                        type={showSecret ? "text" : "password"}
                        value={cfg.s3.secret_access_key}
                        onChange={updS3("secret_access_key")}
                        placeholder="Nuovo · lascia vuoto per non cambiare"
                        className="w-full h-10 px-3 pr-10 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#737373] hover:text-[#0A0A0A]"
                      >
                        {showSecret ? "•••" : "abc"}
                      </button>
                    </div>
                  </Field>
                  <Field label="Public URL base (opzionale)" testid="s3-public-url">
                    <input
                      value={cfg.s3.public_url_base}
                      onChange={updS3("public_url_base")}
                      placeholder="https://cdn.miosito.it (per link pubblici via Cloudflare CDN)"
                      className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm font-mono"
                    />
                  </Field>
                </div>

                {/* Test result */}
                {testResult && (
                  <div className={`rounded-lg border p-3 flex items-start gap-2 text-sm ${
                    testResult.ok ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-red-300 bg-red-50 text-red-900"
                  }`}>
                    {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                    <div>
                      <div className="font-semibold text-xs uppercase tracking-wider">{testResult.ok ? "OK" : "Errore"}</div>
                      <div className="text-xs mt-0.5">{testResult.message}</div>
                    </div>
                  </div>
                )}
                {lastMeta?.last_tested_at && !testResult && (
                  <div className="text-[11px] text-[#737373] flex items-center gap-1">
                    <Info className="w-3 h-3" /> Ultimo test: {new Date(lastMeta.last_tested_at).toLocaleString("it-IT")} · {lastMeta.last_test_ok ? "OK" : "fallito"} · {lastMeta.last_test_msg}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} data-testid="storage-config-cancel" className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">Annulla</button>
          {cfg.provider === "s3" && (
            <button
              onClick={testCurrent}
              disabled={!s3Ready || testing}
              data-testid="storage-config-test"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA] disabled:opacity-60"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Testa connessione
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || (cfg.provider === "s3" && !s3Ready)}
            data-testid="storage-config-save"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-[#0A0A0A] text-white text-sm font-bold hover:bg-[#1D4ED8] disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-[#737373] font-bold mb-1.5" data-testid={testid ? `${testid}-label` : undefined}>
        {label}
      </label>
      {children}
    </div>
  );
}

function StorageDebugModal({ result, onClose, onRerun }) {
  const rows = result?.checks || [];
  const overallOk = result && result.config_ok !== false && rows.every((c) => c.status === "ok" || c.status === "empty" || c.status === "warn");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="storage-debug-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold">Storage · Diagnostica S3</div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight mt-0.5">
              Stato backend S3
              {result && (
                <span className={`ml-3 text-xs px-2 py-1 rounded-full uppercase tracking-wider font-bold ${
                  overallOk ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-red-100 text-red-800 border border-red-200"
                }`}>
                  {overallOk ? "OK" : "Problemi"}
                </span>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onRerun} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E8E4D9] bg-white text-xs hover:bg-[#F5F2EA]">
              <RefreshCw className="w-3 h-3" /> Rilancia
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          {!result ? (
            <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#737373]" /></div>
          ) : result.error ? (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">{result.error}</div>
          ) : (
            <>
              {result.provider !== "s3" && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  {result.message || "Nessun S3 attivo — attiva prima Cloudflare R2 dalla configurazione."}
                </div>
              )}
              {rows.map((c, i) => (
                <div key={c.step || `debug-${i}`} className={`rounded-lg border p-3 ${
                  c.status === "ok" ? "border-emerald-200 bg-emerald-50/50" :
                  c.status === "empty" ? "border-blue-200 bg-blue-50/50" :
                  c.status === "warn" ? "border-amber-200 bg-amber-50/50" :
                  "border-red-200 bg-red-50/50"
                }`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {c.status === "ok" || c.status === "empty" ? <CheckCircle2 className={`w-4 h-4 ${c.status === "empty" ? "text-blue-600" : "text-emerald-600"}`} /> :
                       c.status === "warn" ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
                       <XCircle className="w-4 h-4 text-red-600" />}
                      <div className="font-display font-bold text-sm text-[#0A0A0A]">{c.step}</div>
                    </div>
                    {c.elapsed_ms != null && (
                      <span className="text-[10px] text-[#737373] font-mono">{c.elapsed_ms}ms</span>
                    )}
                  </div>
                  {c.message && <div className="text-xs text-[#4A4A4A] mt-1.5">{c.message}</div>}
                  {c.raw && c.normalized && (
                    <div className="mt-2 text-[11px] font-mono bg-white/70 rounded p-2 space-y-1">
                      <div><span className="text-[#737373]">raw</span>: <span className="text-red-800 line-through">{c.raw}</span></div>
                      <div><span className="text-[#737373]">normalized</span>: <span className="text-emerald-800">{c.normalized}</span></div>
                    </div>
                  )}
                  {c.sample?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-[10px] uppercase tracking-widest text-[#737373] font-bold cursor-pointer">
                        Sample keys ({c.sample.length}{c.key_count_total > c.sample.length ? ` di ${c.key_count_total}+` : ""})
                      </summary>
                      <ul className="mt-1.5 text-[11px] font-mono text-[#4A4A4A] pl-2 space-y-0.5">
                        {c.sample.map((k, j) => (
                          <li key={k.key || `sample-${j}`}>{k.key} <span className="text-[#A3A3A3]">({k.size} B)</span></li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">Chiudi</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────  StorageMigrateModal  ─────────────────────────

function StorageMigrateModal({ onClose, onDone }) {
  const [dryRun, setDryRun] = useState(null); // null | dry report | migrated report
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deleteLocal, setDeleteLocal] = useState(true);

  const runDry = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/storage/migrate", null, {
        params: { dry_run: true, delete_local_after: deleteLocal },
      });
      setDryRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore dry-run");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!window.confirm(`Confermi la migrazione di ${dryRun?.migrated} file (${dryRun?.total_human})?${deleteLocal ? " I file locali verranno eliminati." : ""}`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/storage/migrate", null, {
        params: { dry_run: false, delete_local_after: deleteLocal },
      });
      toast.success(`Migrazione completata · ${data.migrated} file · ${data.total_human}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore migrazione");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => { runDry(); }, []);

  const summary = dryRun ? (
    <div className="grid grid-cols-3 gap-3">
      <div className="rounded-lg border border-[#E8E4D9] p-3">
        <div className="text-[10px] uppercase font-bold text-[#737373]">Da migrare</div>
        <div className="font-display font-extrabold text-2xl mt-0.5 tabular-nums text-[#0A0A0A]">{dryRun.migrated}</div>
      </div>
      <div className="rounded-lg border border-[#E8E4D9] p-3">
        <div className="text-[10px] uppercase font-bold text-[#737373]">Peso totale</div>
        <div className="font-display font-extrabold text-2xl mt-0.5 tabular-nums text-[#0A0A0A]">{dryRun.total_human}</div>
      </div>
      <div className="rounded-lg border border-[#E8E4D9] p-3">
        <div className="text-[10px] uppercase font-bold text-[#737373]">Errori</div>
        <div className="font-display font-extrabold text-2xl mt-0.5 tabular-nums text-red-700">{dryRun.failed}</div>
      </div>
    </div>
  ) : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="storage-migrate-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-orange-700 font-bold">Storage · Migrazione</div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight mt-0.5">Migra file locali → S3</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-2 text-sm text-blue-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              La migrazione copia tutti i file dal filesystem locale al bucket S3 attivo,
              aggiornando i riferimenti nel database. <strong>Il bucket deve essere già configurato e testato</strong>.
              Il DB viene aggiornato solo dopo un upload riuscito → operazione sicura anche in caso di crash a metà.
            </div>
          </div>

          {loading ? (
            <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#737373]" /></div>
          ) : (
            <>
              {summary}
              {dryRun?.details?.length > 0 && (
                <div className="rounded-xl border border-[#E8E4D9] overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="migrate-preview-table">
                    <thead className="bg-[#F5F2EA] text-[9px] uppercase tracking-widest font-bold text-[#737373] sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Directory</th>
                        <th className="text-left px-3 py-2">File</th>
                        <th className="text-left px-3 py-2 w-20">Size</th>
                        <th className="text-left px-3 py-2 w-24">Ref DB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRun.details.slice(0, 200).map((d, i) => (
                        <tr key={`${d.dir}::${d.file}` || `migrate-${i}`} className="border-t border-[#E8E4D9]/70">
                          <td className="px-3 py-1.5 font-mono text-[10px]">{d.dir}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px] truncate max-w-[280px]" title={d.file}>{d.file}</td>
                          <td className="px-3 py-1.5 tabular-nums text-[10px]">{d.size_human}</td>
                          <td className="px-3 py-1.5">
                            {d.has_db_ref ? (
                              <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded uppercase">sì</span>
                            ) : (
                              <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded uppercase">no · orfano</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="migrate-delete-local-toggle">
                <input
                  type="checkbox"
                  checked={deleteLocal}
                  onChange={(e) => setDeleteLocal(e.target.checked)}
                  className="w-4 h-4 accent-[#1D4ED8]"
                />
                <span>
                  Elimina i file locali dopo l&apos;upload S3 (raccomandato — libera spazio del container)
                </span>
              </label>
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">Annulla</button>
          <button
            onClick={runDry}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA] disabled:opacity-60"
            data-testid="migrate-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Rilancia dry-run
          </button>
          <button
            onClick={apply}
            disabled={!dryRun || dryRun.migrated === 0 || applying}
            data-testid="migrate-apply"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-orange-600 text-white text-sm font-bold hover:bg-orange-700 disabled:opacity-60"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
            {applying ? "Migrazione in corso…" : `Migra ${dryRun?.migrated ?? 0} file`}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkedDocInfo({ dir, doc }) {
  if (!doc) return <span className="italic text-[#A3A3A3]">—</span>;
  // Route per collection
  if (dir === "uploads" || dir === "xml_output") {
    return (
      <div className="flex flex-col gap-0.5">
        <Link
          to={`/app/admin/invoices?q=${encodeURIComponent(supportCode(doc.id))}`}
          className="font-mono font-bold text-[#1D4ED8] hover:underline inline-flex items-center gap-1"
          data-testid="storage-linked-link"
        >
          {supportCode(doc.id)} <ExternalLink className="w-2.5 h-2.5" />
        </Link>
        <div className="text-[#737373] text-[11px] truncate max-w-[260px]">
          {doc.autofattura_number || doc.status || "—"} · {doc.user_email || "—"}
        </div>
      </div>
    );
  }
  if (dir === "platform_invoices_xml") {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono font-bold text-[#0A0A0A]">{doc.full_number || doc.id}</span>
        <div className="text-[#737373] text-[11px] truncate max-w-[260px]">{doc.package_name || "—"}</div>
      </div>
    );
  }
  if (dir === "proforma_pdfs") {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-mono font-bold text-[#0A0A0A]">{doc.proforma_number || doc.id}</span>
        <div className="text-[#737373] text-[11px] truncate max-w-[260px]">{doc.package_name || "—"}</div>
      </div>
    );
  }
  return <span className="text-[#737373] text-xs">{doc.id}</span>;
}

// ─────────────────────────  StorageRepairModal  ──────────────────────────

function StorageRepairModal({ onClose, onDone }) {
  const [dryRun, setDryRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [missingPdfs, setMissingPdfs] = useState(null);

  const runDry = async () => {
    setLoading(true);
    try {
      const [{ data: xml }, { data: pdfs }] = await Promise.all([
        api.post("/admin/storage/repair-xml", null, { params: { dry_run: true } }),
        api.get("/admin/storage/missing-pdfs"),
      ]);
      setDryRun(xml);
      setMissingPdfs(pdfs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore dry-run");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!window.confirm(`Confermi la rigenerazione di ${dryRun?.regenerated} XML?`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/storage/repair-xml", null, { params: { dry_run: false } });
      toast.success(`Riparazione completata · ${data.regenerated} XML rigenerati`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore riparazione");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => { runDry(); }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="storage-repair-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-700 font-bold">Storage · Riparazione</div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight mt-0.5">Ripara XML mancanti</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-2 text-sm text-blue-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              Scansiona tutte le fatture con <code className="font-mono">xml_path</code> valorizzato ma file mancante sul backend attivo,
              e rigenera l&apos;XML da <code className="font-mono">extracted + company_profile</code>. Utile dopo una migrazione S3 fallita a metà.
              I <strong>PDF originali</strong> non sono rigenerabili — se mancano, l&apos;utente deve ri-caricarli.
            </div>
          </div>

          {loading ? (
            <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#737373]" /></div>
          ) : dryRun ? (
            <>
              <div className="grid grid-cols-4 gap-3">
                <StatCard label="Fatture con XML" value={dryRun.checked} />
                <StatCard label="Già OK" value={dryRun.already_ok} tone="emerald" />
                <StatCard label="Da rigenerare" value={dryRun.regenerated} tone="orange" />
                <StatCard label="Bloccate" value={(dryRun.failed_no_company || 0) + (dryRun.failed_regen || 0)} tone="red" />
              </div>
              {dryRun.regenerated > 0 && (
                <div className="rounded-xl border border-[#E8E4D9] overflow-hidden max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F5F2EA] text-[9px] uppercase tracking-widest font-bold text-[#737373] sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Invoice</th>
                        <th className="text-left px-3 py-2">Autofattura</th>
                        <th className="text-left px-3 py-2 w-24">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRun.details.slice(0, 100).map((d, i) => (
                        <tr key={d.invoice_id || `repair-${i}`} className="border-t border-[#E8E4D9]/70">
                          <td className="px-3 py-1.5 font-mono text-[10px]">{d.invoice_id?.slice(0, 8)}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px]">{d.af_number || "—"}</td>
                          <td className="px-3 py-1.5">
                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${
                              d.status === "would_regen" ? "border-orange-300 bg-orange-50 text-orange-800" :
                              d.status === "no_company" ? "border-red-300 bg-red-50 text-red-800" :
                              d.status === "regenerated" ? "border-emerald-300 bg-emerald-50 text-emerald-800" :
                              "border-zinc-300 bg-zinc-50 text-zinc-700"
                            }`}>
                              {d.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {missingPdfs?.count > 0 && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-700" />
                    <div className="font-display font-bold text-sm text-red-900">
                      {missingPdfs.count} PDF originali persi (non rigenerabili)
                    </div>
                  </div>
                  <div className="text-xs text-red-800 space-y-1">
                    {missingPdfs.items.slice(0, 5).map((p, i) => (
                      <div key={p.invoice_id || `missing-${i}`} className="font-mono">
                        {p.invoice_id?.slice(0, 8)} · <strong>{p.user_email}</strong> · {p.file_name || "—"} · <em>{p.status}</em>
                      </div>
                    ))}
                    {missingPdfs.count > 5 && <div className="italic">…e altri {missingPdfs.count - 5}</div>}
                    <div className="mt-2 pt-2 border-t border-red-200 text-red-900">
                      Contatta questi utenti per re-upload del PDF originale.
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-sm text-[#737373] italic">Nessun risultato — clicca Rilancia.</div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">Chiudi</button>
          <button
            onClick={runDry}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Rilancia scan
          </button>
          <button
            onClick={apply}
            disabled={!dryRun || dryRun.regenerated === 0 || applying}
            data-testid="storage-repair-apply"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {applying ? "Rigenerazione…" : `Rigenera ${dryRun?.regenerated ?? 0} XML`}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "zinc" }) {
  const tones = {
    zinc:    "text-[#0A0A0A]",
    emerald: "text-emerald-700",
    orange:  "text-orange-700",
    red:     "text-red-700",
  };
  return (
    <div className="rounded-lg border border-[#E8E4D9] p-3">
      <div className="text-[10px] uppercase font-bold text-[#737373]">{label}</div>
      <div className={`font-display font-extrabold text-2xl mt-0.5 tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

// ─────────────────────────  StorageKeyRepairModal  ───────────────────────

function StorageKeyRepairModal({ onClose, onDone }) {
  const [dryRun, setDryRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runDry = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/storage/repair-key-prefix", null, { params: { dry_run: true } });
      setDryRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore dry-run");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!window.confirm(`Confermi il rename di ${dryRun?.to_fix} key S3?`)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/storage/repair-key-prefix", null, { params: { dry_run: false } });
      toast.success(`Riparazione key completata · ${data.fixed} rinominate · ${data.failed} fallite`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rename");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => { runDry(); }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="storage-key-repair-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-purple-700 font-bold">Storage · Riparazione key</div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight mt-0.5">Ripara key S3 mal-prefissate</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 flex items-start gap-2 text-sm text-purple-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              A causa del bug &quot;endpoint URL con bucket dentro&quot;, alcuni file sono finiti su R2 con
              key <code className="font-mono text-[11px]">&lt;bucket&gt;/uploads/xxx.pdf</code> invece di <code className="font-mono text-[11px]">uploads/xxx.pdf</code>.
              Questa operazione fa <strong>CopyObject + DeleteObject</strong> per ogni key mal-prefissata.
              I DB refs sono già nel formato corretto → dopo il rename i file appariranno normalmente nella lista Storage.
            </div>
          </div>

          {loading ? (
            <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#737373]" /></div>
          ) : dryRun ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Oggetti bucket" value={dryRun.total_objects} />
                <StatCard label="Da rinominare" value={dryRun.to_fix} tone="orange" />
                <StatCard label="Già OK" value={(dryRun.total_objects || 0) - (dryRun.to_fix || 0)} tone="emerald" />
              </div>
              {(dryRun.sample || []).length > 0 && (
                <div className="rounded-xl border border-[#E8E4D9] overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F5F2EA] text-[9px] uppercase tracking-widest font-bold text-[#737373] sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Vecchia key</th>
                        <th className="text-left px-3 py-2">Nuova key</th>
                        <th className="text-left px-3 py-2 w-20">Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dryRun.sample.map((s, i) => (
                        <tr key={s.old_key || `keyrepair-${i}`} className="border-t border-[#E8E4D9]/70">
                          <td className="px-3 py-1.5 font-mono text-[10px] truncate max-w-[300px]" title={s.old_key}>
                            <span className="line-through opacity-70">{s.old_key}</span>
                          </td>
                          <td className="px-3 py-1.5 font-mono text-[10px] truncate max-w-[300px] text-emerald-800" title={s.new_key}>
                            {s.new_key}
                          </td>
                          <td className="px-3 py-1.5 tabular-nums text-[10px]">{s.size} B</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-sm text-[#737373] italic">Nessun risultato — clicca Rilancia.</div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]">Chiudi</button>
          <button
            onClick={runDry}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Rilancia scan
          </button>
          <button
            onClick={apply}
            disabled={!dryRun || (dryRun.to_fix || 0) === 0 || applying}
            data-testid="storage-key-repair-apply"
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 disabled:opacity-60"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {applying ? "Rinominando…" : `Rinomina ${dryRun?.to_fix ?? 0} key`}
          </button>
        </div>
      </div>
    </div>
  );
}
